/**
 * Selection (DS foundation): MultiSelect, Menu and DateRangePicker. Built on base-ui (already a dependency:
 * the same engine as Select, Dialog and Popover), so keyboard navigation, typeahead, Escape, outside press,
 * focus return and collision-aware positioning come from one place. Every popup is the shared `.ffc-menu`
 * surface, so they all open with the one menu motion (controls.css: scale 0.97 + fade, 180ms in, 120ms out).
 */
import { Menu as BaseMenu } from "@base-ui/react/menu";
import { Popover } from "@base-ui/react/popover";
import { Select as BaseSelect } from "@base-ui/react/select";
import { useEffect, useId, useMemo, useRef, useState, type KeyboardEvent, type ReactNode } from "react";
import { addDays, isoDate, monthGrid, parseIso, usePhone } from "./components.js";
import type { SelectOption } from "./controls.js";

const cx = (...parts: Array<string | false | null | undefined>) => parts.filter(Boolean).join(" ");

function Chevron() {
  return (
    <svg className="ffc-chev" viewBox="0 0 16 16" aria-hidden="true">
      <path d="M4.5 6.25 8 9.75l3.5-3.5" />
    </svg>
  );
}
function Check() {
  return (
    <svg className="ffc-check" viewBox="0 0 16 16" aria-hidden="true">
      <path d="m3.5 8.5 3 3 6-7" />
    </svg>
  );
}
function CalendarGlyph() {
  return (
    <svg viewBox="0 0 16 16" aria-hidden="true">
      <rect x="2.5" y="3.5" width="11" height="10" rx="2" />
      <path d="M2.5 6.5h11M5.5 2v3M10.5 2v3" />
    </svg>
  );
}

function FieldMessage({ id, error, hint }: { id: string; error?: ReactNode; hint?: ReactNode }) {
  return (
    <>
      <p className="ffc-msg ffc-msg--error" id={`${id}-err`} aria-live="polite">
        {error || null}
      </p>
      {!error && hint ? (
        <p className="ffc-msg" id={`${id}-hint`}>
          {hint}
        </p>
      ) : null}
    </>
  );
}

/* ============================================================ MultiSelect */

/** What a multi-select trigger reads: nothing -> placeholder, one -> its label, more -> "First +2". */
export function multiSummary(labels: readonly string[], placeholder: string): string {
  if (labels.length === 0) return placeholder;
  if (labels.length === 1) return labels[0]!;
  return `${labels[0]} +${labels.length - 1}`;
}

export type MultiSelectProps<V extends string = string> = {
  label: string;
  options: ReadonlyArray<SelectOption<V>>;
  value: ReadonlyArray<V>;
  onChange: (value: V[]) => void;
  /** "field": floating label, form height. "toolbar": compact "Label  value", highlighted while anything is chosen. */
  variant?: "field" | "toolbar";
  /** Shown when nothing is chosen (toolbar default "Any"). */
  placeholder?: string;
  size?: "sm" | "md";
  name?: string;
  id?: string;
  disabled?: boolean;
  optional?: boolean;
  hint?: ReactNode;
  error?: ReactNode;
  className?: string;
};

/**
 * Pick several options (carriers, statuses, regions). base-ui listbox in multiple mode: the menu stays open while
 * you pick, Space/Enter toggles, typeahead jumps, "Clear" empties the choice. The trigger summarises ("XPO +2").
 */
export function MultiSelect<V extends string = string>({
  label,
  options,
  value,
  onChange,
  variant = "field",
  placeholder,
  size = "md",
  name,
  id,
  disabled,
  optional,
  hint,
  error,
  className,
}: MultiSelectProps<V>) {
  const auto = useId();
  const baseId = id ?? `m${auto.replace(/:/g, "")}`;
  const toolbar = variant === "toolbar";
  const empty = placeholder ?? (toolbar ? "Any" : "Choose…");
  const chosen = options.filter((o) => value.includes(o.value));
  const summary = multiSummary(
    chosen.map((o) => o.label),
    empty,
  );
  const items = useMemo(() => options.map((o) => ({ value: o.value, label: o.label })), [options]);
  return (
    <div
      className={cx(
        "ffc-field",
        toolbar ? "ffc-field--toolbar" : "ffc-field--select",
        !toolbar && size === "sm" ? "ffc-field--sm" : null,
        chosen.length ? "is-filled" : null,
        toolbar && chosen.length ? "is-active" : null,
        error ? "is-invalid" : null,
        disabled ? "is-disabled" : null,
        "ffx-multi",
        className,
      )}
    >
      <BaseSelect.Root<V, true>
        multiple
        items={items}
        value={value as V[]}
        onValueChange={(v) => onChange([...(v ?? [])])}
        name={name}
        disabled={disabled}
        modal={false}
      >
        <div className="ffc-box">
          <BaseSelect.Trigger
            id={baseId}
            className="ffc-trigger"
            aria-label={toolbar ? `${label}: ${chosen.length ? chosen.map((o) => o.label).join(", ") : empty}` : undefined}
            aria-describedby={error ? `${baseId}-err` : hint ? `${baseId}-hint` : undefined}
            aria-invalid={error ? true : undefined}
          >
            {toolbar ? (
              <span className="ffc-trigger__text">
                <span className="ffc-trigger__label">{label}</span>
                <span className="ffc-trigger__value">{summary}</span>
              </span>
            ) : (
              <span className="ffc-trigger__value" title={chosen.map((o) => o.label).join(", ")}>
                {chosen.length ? summary : <span className="ffc-placeholder">{empty}</span>}
              </span>
            )}
            {chosen.length > 1 && !toolbar ? (
              <span className="ffc-count" aria-hidden="true">
                {chosen.length}
              </span>
            ) : null}
            <BaseSelect.Icon className="ffc-trigger__icon">
              <Chevron />
            </BaseSelect.Icon>
          </BaseSelect.Trigger>
          {toolbar ? null : (
            <BaseSelect.Label className="ffc-label">
              {label}
              {optional ? <span className="ffc-optional"> Optional</span> : null}
            </BaseSelect.Label>
          )}
        </div>
        <BaseSelect.Portal>
          <BaseSelect.Positioner className="ffc-positioner" sideOffset={6} alignItemWithTrigger={false} collisionPadding={12}>
            <BaseSelect.Popup className="ffc-menu ffx-multi__menu">
              <BaseSelect.List className="ffc-menu__list">
                {options.map((o) => (
                  <BaseSelect.Item key={o.value} value={o.value} disabled={o.disabled} className="ffc-opt ffx-multi__opt">
                    <span className="ffx-multi__box" aria-hidden="true">
                      <Check />
                    </span>
                    {o.icon ? (
                      <span className="ffc-opt__icon" aria-hidden="true">
                        {o.icon}
                      </span>
                    ) : null}
                    <BaseSelect.ItemText className="ffc-opt__text">{o.label}</BaseSelect.ItemText>
                    {o.meta !== undefined ? <span className="ffc-opt__meta">{o.meta}</span> : null}
                  </BaseSelect.Item>
                ))}
              </BaseSelect.List>
              {chosen.length ? (
                <div className="ffx-multi__foot">
                  <span className="ffx-multi__n">{chosen.length} selected</span>
                  <button type="button" className="ffc-link" onClick={() => onChange([])}>
                    Clear
                  </button>
                </div>
              ) : null}
            </BaseSelect.Popup>
          </BaseSelect.Positioner>
        </BaseSelect.Portal>
      </BaseSelect.Root>
      {toolbar ? null : <FieldMessage id={baseId} error={error} hint={hint} />}
    </div>
  );
}

/* ============================================================ Menu */

export type MenuAction = {
  type?: "item";
  key?: string;
  label: string;
  onSelect?: () => void;
  /** Navigates instead (rendered as a link item: middle-click and Cmd-click work). */
  href?: string;
  icon?: ReactNode;
  /** Shown right-aligned ("⌘K"); purely a hint. */
  shortcut?: string;
  danger?: boolean;
  disabled?: boolean;
};
export type MenuCheck = { type: "checkbox"; key?: string; label: string; checked: boolean; onCheckedChange: (checked: boolean) => void; disabled?: boolean };
export type MenuSeparator = { type: "separator"; key?: string };
export type MenuGroupLabel = { type: "label"; key?: string; label: string };
export type MenuEntry = MenuAction | MenuCheck | MenuSeparator | MenuGroupLabel;

export type MenuProps = {
  /** Trigger content (text, or an icon with `triggerLabel`). */
  trigger: ReactNode;
  /** Accessible name when the trigger is an icon. */
  triggerLabel?: string;
  items: ReadonlyArray<MenuEntry>;
  /** Trigger look: a button variant, or "icon" (square, quiet: row "more" actions). */
  look?: "secondary" | "ghost" | "primary" | "icon";
  size?: "sm" | "md";
  align?: "start" | "center" | "end";
  side?: "top" | "bottom";
  disabled?: boolean;
  className?: string;
};

/** The "more" glyph for icon menus. */
export function MoreIcon() {
  return (
    <svg className="ffc-ico" viewBox="0 0 16 16" aria-hidden="true">
      <circle cx="3.5" cy="8" r="1.1" fill="currentColor" stroke="none" />
      <circle cx="8" cy="8" r="1.1" fill="currentColor" stroke="none" />
      <circle cx="12.5" cy="8" r="1.1" fill="currentColor" stroke="none" />
    </svg>
  );
}

/**
 * Action menu (row actions, "More", column visibility). WAI-ARIA menu button: Enter/Space/ArrowDown open on the
 * first item, arrows move, typeahead jumps, Escape closes and returns focus to the trigger. Checkbox items keep
 * the menu open. Danger items are red and sit last by convention.
 */
export function Menu({ trigger, triggerLabel, items, look = "secondary", size = "md", align = "end", side = "bottom", disabled, className }: MenuProps) {
  const btn = look === "icon" ? cx("ffx-btn ffx-btn--ghost ffx-menu__icon", size === "sm" && "ffx-btn--sm") : cx("ffx-btn", look !== "secondary" && `ffx-btn--${look}`, size === "sm" && "ffx-btn--sm");
  return (
    <BaseMenu.Root modal={false}>
      <BaseMenu.Trigger className={cx(btn, "ffx-menu__trigger", className)} aria-label={triggerLabel} disabled={disabled}>
        {trigger}
        {look === "icon" ? null : <Chevron />}
      </BaseMenu.Trigger>
      <BaseMenu.Portal>
        <BaseMenu.Positioner className="ffc-positioner" sideOffset={6} align={align} side={side} collisionPadding={12}>
          <BaseMenu.Popup className="ffc-menu ffx-menu">
            {items.map((it, i) => {
              const key = it.key ?? `${it.type ?? "item"}-${"label" in it ? it.label : i}`;
              if (it.type === "separator") return <BaseMenu.Separator key={key} className="ffc-sep" />;
              if (it.type === "label") return <div key={key} className="ffx-menu__label" role="presentation">{it.label}</div>;
              if (it.type === "checkbox") {
                return (
                  <BaseMenu.CheckboxItem key={key} className="ffc-opt ffx-menu__item" checked={it.checked} onCheckedChange={it.onCheckedChange} disabled={it.disabled} closeOnClick={false}>
                    <span className="ffx-multi__box" aria-hidden="true">
                      <Check />
                    </span>
                    <span className="ffc-opt__text">{it.label}</span>
                  </BaseMenu.CheckboxItem>
                );
              }
              const body = (
                <>
                  {it.icon ? (
                    <span className="ffc-opt__icon" aria-hidden="true">
                      {it.icon}
                    </span>
                  ) : null}
                  <span className="ffc-opt__text">{it.label}</span>
                  {it.shortcut ? <kbd className="ffc-opt__meta">{it.shortcut}</kbd> : null}
                </>
              );
              const cls = cx("ffc-opt ffx-menu__item", it.danger && "ffx-menu__item--danger");
              return it.href ? (
                <BaseMenu.LinkItem key={key} className={cls} href={it.href} onClick={it.onSelect}>
                  {body}
                </BaseMenu.LinkItem>
              ) : (
                <BaseMenu.Item key={key} className={cls} disabled={it.disabled} onClick={it.onSelect} label={it.label}>
                  {body}
                </BaseMenu.Item>
              );
            })}
          </BaseMenu.Popup>
        </BaseMenu.Positioner>
      </BaseMenu.Portal>
    </BaseMenu.Root>
  );
}

/* ============================================================ DateRangePicker */

export type DateRange = { start: string | null; end: string | null };
export type RangePreset = { label: string; range: DateRange };

/** Default presets relative to `now`: today, last 7 / 30 / 90 days, this month, last month. */
export function rangePresets(now: Date): RangePreset[] {
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const iso = isoDate;
  const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
  const lastStart = new Date(today.getFullYear(), today.getMonth() - 1, 1);
  const lastEnd = addDays(monthStart, -1);
  return [
    { label: "Today", range: { start: iso(today), end: iso(today) } },
    { label: "Last 7 days", range: { start: iso(addDays(today, -6)), end: iso(today) } },
    { label: "Last 30 days", range: { start: iso(addDays(today, -29)), end: iso(today) } },
    { label: "Last 90 days", range: { start: iso(addDays(today, -89)), end: iso(today) } },
    { label: "This month", range: { start: iso(monthStart), end: iso(today) } },
    { label: "Last month", range: { start: iso(lastStart), end: iso(lastEnd) } },
  ];
}

/** "Oct 1 – Oct 14, 2026" style label; same-day reads once; open ends read "From…"/"Until…". */
export function formatRange(r: DateRange, now: Date = new Date()): string {
  const fmt = (iso: string, withYear: boolean) => parseIso(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", ...(withYear ? { year: "numeric" } : {}) });
  if (!r.start && !r.end) return "";
  if (r.start && !r.end) return `From ${fmt(r.start, parseIso(r.start).getFullYear() !== now.getFullYear())}`;
  if (!r.start && r.end) return `Until ${fmt(r.end, parseIso(r.end).getFullYear() !== now.getFullYear())}`;
  const s = r.start!;
  const e = r.end!;
  const sameYear = parseIso(s).getFullYear() === parseIso(e).getFullYear();
  const showYear = !sameYear || parseIso(e).getFullYear() !== now.getFullYear();
  if (s === e) return fmt(s, showYear);
  return `${fmt(s, showYear && !sameYear)} – ${fmt(e, showYear)}`;
}

/** Two picks make a range: the second pick before the first swaps them. Exported for tests. */
export function pickRange(current: { anchor: string | null }, iso: string): { range: DateRange; anchor: string | null } {
  if (!current.anchor) return { range: { start: iso, end: null }, anchor: iso };
  const [a, b] = current.anchor <= iso ? [current.anchor, iso] : [iso, current.anchor];
  return { range: { start: a, end: b }, anchor: null };
}

/** Keyboard movement inside the month grid (days). Exported for tests. */
export function gridStep(key: string, shift: boolean): number | "month+" | "month-" | "weekStart" | "weekEnd" | null {
  switch (key) {
    case "ArrowLeft": return -1;
    case "ArrowRight": return 1;
    case "ArrowUp": return -7;
    case "ArrowDown": return 7;
    case "PageUp": return shift ? -365 : "month-";
    case "PageDown": return shift ? 365 : "month+";
    case "Home": return "weekStart";
    case "End": return "weekEnd";
    default: return null;
  }
}

const DOW = ["S", "M", "T", "W", "T", "F", "S"];

export type DateRangePickerProps = {
  label: string;
  value: DateRange;
  onChange: (range: DateRange) => void;
  /** "field": floating label, form height. "toolbar": compact "Label  Oct 1 – Oct 14" for filter rows. */
  variant?: "field" | "toolbar";
  /** Quick ranges beside the calendar (default: rangePresets(now)); [] hides them. */
  presets?: readonly RangePreset[];
  /** Earliest / latest selectable day (ISO). */
  min?: string;
  max?: string;
  now?: Date;
  size?: "sm" | "md";
  placeholder?: string;
  hint?: ReactNode;
  error?: ReactNode;
  optional?: boolean;
  disabled?: boolean;
  id?: string;
  className?: string;
};

/**
 * Pick a start and end day. Popover (bottom-anchored, collision aware; full-width under the trigger on phones):
 * presets on the side, one or two months of grid. Click (or Enter) a start, then an end; hovering previews the
 * range. Arrows move by day/week, PageUp/PageDown by month (Shift: year), Home/End to the week edges, Escape
 * closes and returns focus to the trigger. "Clear" empties both ends.
 */
export function DateRangePicker({
  label,
  value,
  onChange,
  variant = "field",
  presets: presetsProp,
  min,
  max,
  now = new Date(),
  size = "md",
  placeholder = "Any time",
  hint,
  error,
  optional,
  disabled,
  id,
  className,
}: DateRangePickerProps) {
  const auto = useId();
  const baseId = id ?? `r${auto.replace(/:/g, "")}`;
  const phone = usePhone();
  const toolbar = variant === "toolbar";
  const [open, setOpen] = useState(false);
  const [anchor, setAnchor] = useState<string | null>(null);
  const [hover, setHover] = useState<string | null>(null);
  const start0 = value.start ? parseIso(value.start) : value.end ? parseIso(value.end) : now;
  const [focus, setFocus] = useState<Date>(start0);
  const [view, setView] = useState({ y: start0.getFullYear(), m: start0.getMonth() });
  const gridRef = useRef<HTMLDivElement>(null);
  const presets = presetsProp ?? rangePresets(now);
  const months = phone ? 1 : 2;
  const shown = formatRange(value, now);
  const filled = !!shown;

  useEffect(() => {
    if (!open) return;
    const t = requestAnimationFrame(() => gridRef.current?.querySelector<HTMLButtonElement>(`[data-iso="${isoDate(focus)}"]`)?.focus());
    return () => cancelAnimationFrame(t);
  }, [open, focus]);

  const allowed = (iso: string) => (!min || iso >= min) && (!max || iso <= max);
  const onOpenChange = (o: boolean) => {
    if (o) {
      const s = value.start ? parseIso(value.start) : now;
      setFocus(s);
      setView({ y: s.getFullYear(), m: s.getMonth() });
      setAnchor(null);
      setHover(null);
    }
    setOpen(o);
  };
  const pick = (iso: string) => {
    if (!allowed(iso)) return;
    const next = pickRange({ anchor }, iso);
    setAnchor(next.anchor);
    onChange(next.range);
    if (!next.anchor) setOpen(false);
  };
  const moveTo = (d: Date) => {
    setFocus(d);
    const first = new Date(view.y, view.m, 1);
    const last = new Date(view.y, view.m + months, 0);
    if (d < first || d > last) setView({ y: d.getFullYear(), m: d.getMonth() - (d > last ? months - 1 : 0) });
  };
  const onGridKey = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      pick(isoDate(focus));
      return;
    }
    const s = gridStep(e.key, e.shiftKey);
    if (s === null) return;
    e.preventDefault();
    if (typeof s === "number") moveTo(addDays(focus, s));
    else if (s === "month+" || s === "month-") moveTo(new Date(focus.getFullYear(), focus.getMonth() + (s === "month+" ? 1 : -1), Math.min(focus.getDate(), 28)));
    else if (s === "weekStart") moveTo(addDays(focus, -focus.getDay()));
    else moveTo(addDays(focus, 6 - focus.getDay()));
  };
  // Preview: while choosing the end, the range follows the pointer (or the focused day from the keyboard).
  const lo = anchor ? (hover && hover < anchor ? hover : anchor) : value.start;
  const hi = anchor ? (hover && hover > anchor ? hover : anchor) : value.end;
  const todayIso = isoDate(now);
  const focusIso = isoDate(focus);

  const monthView = (offset: number) => {
    const first = new Date(view.y, view.m + offset, 1);
    const y = first.getFullYear();
    const m = first.getMonth();
    const name = first.toLocaleDateString("en-US", { month: "long", year: "numeric" });
    const grid = monthGrid(y, m);
    return (
      <div className="ffx-range__month" key={`${y}-${m}`}>
        <div className="ffx-range__mhead">
          {offset === 0 ? (
            <button type="button" className="ffx-date__nav" aria-label="Previous month" onClick={() => setView({ y: new Date(y, m - 1, 1).getFullYear(), m: new Date(y, m - 1, 1).getMonth() })}>
              <svg viewBox="0 0 16 16" aria-hidden="true"><path d="M10 3 5 8l5 5" /></svg>
            </button>
          ) : <span className="ffx-range__navspace" />}
          <span className="ffx-date__month">{name}</span>
          {offset === months - 1 ? (
            <button type="button" className="ffx-date__nav" aria-label="Next month" onClick={() => setView({ y: new Date(view.y, view.m + 1, 1).getFullYear(), m: new Date(view.y, view.m + 1, 1).getMonth() })}>
              <svg viewBox="0 0 16 16" aria-hidden="true"><path d="m6 3 5 5-5 5" /></svg>
            </button>
          ) : <span className="ffx-range__navspace" />}
        </div>
        <div role="grid" aria-label={name} className="ffx-date__grid">
          <div role="row" className="ffx-date__row">
            {DOW.map((d, i) => <span key={i} role="columnheader" className="ffx-date__dow" aria-hidden="true">{d}</span>)}
          </div>
          {[0, 1, 2, 3, 4, 5].map((w) => (
            <div role="row" className="ffx-date__row" key={w}>
              {grid.slice(w * 7, w * 7 + 7).map((d) => {
                const iso = isoDate(d);
                const out = d.getMonth() !== m;
                if (out) return <span role="gridcell" key={iso} className="ffx-range__blank" />;
                const ok = allowed(iso);
                const inRange = !!lo && !!hi && iso > lo && iso < hi;
                const isStart = !!lo && iso === lo;
                const isEnd = !!hi && iso === hi;
                const sel = isStart || isEnd;
                return (
                  <span role="gridcell" key={iso} aria-selected={sel || inRange} className={cx("ffx-range__cell", inRange && "is-in", isStart && "is-start", isEnd && "is-end", isStart && isEnd && "is-single")}>
                    <button
                      type="button"
                      data-iso={iso}
                      tabIndex={iso === focusIso ? 0 : -1}
                      aria-disabled={!ok || undefined}
                      aria-label={`${d.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" })}${isStart ? ", start" : ""}${isEnd ? ", end" : ""}${ok ? "" : ", unavailable"}`}
                      className={cx("ffx-date__day", iso === todayIso && "is-today", sel && "is-selected", !ok && "is-off")}
                      onClick={() => { setFocus(d); pick(iso); }}
                      onPointerEnter={() => setHover(iso)}
                      onFocus={() => { if (anchor) setHover(iso); }}
                    >
                      {d.getDate()}
                    </button>
                  </span>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    );
  };

  const trigId = `${baseId}-trigger`;
  return (
    <div className={cx("ffc-field", toolbar ? "ffc-field--toolbar" : "ffc-field--select", !toolbar && size === "sm" && "ffc-field--sm", filled && "is-filled", toolbar && filled && "is-active", error ? "is-invalid" : null, disabled && "is-disabled", "ffx-range", className)}>
      <Popover.Root open={open} onOpenChange={onOpenChange}>
        <div className="ffc-box">
          <Popover.Trigger
            id={trigId}
            className="ffc-trigger"
            disabled={disabled}
            aria-label={toolbar ? `${label}: ${shown || placeholder}` : undefined}
            aria-labelledby={toolbar ? undefined : `${baseId}-label ${baseId}-value`}
            aria-describedby={error ? `${baseId}-err` : hint ? `${baseId}-hint` : undefined}
            aria-invalid={error ? true : undefined}
            data-value={value.start || value.end ? `${value.start ?? ""}/${value.end ?? ""}` : ""}
          >
            {toolbar ? (
              <span className="ffc-trigger__text">
                <span className="ffc-trigger__label">{label}</span>
                <span className="ffc-trigger__value">{shown || placeholder}</span>
              </span>
            ) : (
              <span className="ffc-trigger__value" id={`${baseId}-value`}>{shown}</span>
            )}
            <span className="ffc-trigger__icon ffx-date__icon"><CalendarGlyph /></span>
          </Popover.Trigger>
          {toolbar ? null : (
            <label className="ffc-label" id={`${baseId}-label`} htmlFor={trigId}>
              {label}
              {optional ? <span className="ffc-optional"> Optional</span> : null}
            </label>
          )}
        </div>
        <Popover.Portal>
          <Popover.Positioner className="ffc-positioner" side="bottom" align="start" sideOffset={6} collisionPadding={12}>
            <Popover.Popup className="ffc-menu ffx-range__pop" aria-label={`Choose ${label.toLowerCase()}`} initialFocus={false}>
              {presets.length ? (
                <div className="ffx-range__presets" role="group" aria-label="Quick ranges">
                  {presets.map((p) => {
                    const on = p.range.start === value.start && p.range.end === value.end;
                    return (
                      <button key={p.label} type="button" className="ffx-range__preset" aria-pressed={on} onClick={() => { onChange(p.range); setOpen(false); }}>
                        {p.label}
                      </button>
                    );
                  })}
                </div>
              ) : null}
              <div className="ffx-range__body">
                <div className="ffx-range__months" ref={gridRef} onKeyDown={onGridKey} onPointerLeave={() => setHover(null)}>
                  {Array.from({ length: months }, (_, i) => monthView(i))}
                </div>
                <div className="ffx-range__foot">
                  <span className="ffx-range__status" aria-live="polite">
                    {anchor ? "Now choose the end date" : shown || "Choose a start date"}
                  </span>
                  {value.start || value.end ? (
                    <button type="button" className="ffc-link" onClick={() => { onChange({ start: null, end: null }); setAnchor(null); setOpen(false); }}>
                      Clear
                    </button>
                  ) : null}
                </div>
              </div>
            </Popover.Popup>
          </Popover.Positioner>
        </Popover.Portal>
      </Popover.Root>
      {toolbar ? null : <FieldMessage id={baseId} error={error} hint={hint} />}
    </div>
  );
}
